import React, { useState } from 'react';
import { TextField, Button, Box, Typography, Divider } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { enqueueSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { AuthForm } from './AuthForm';
import { authService } from '../../services/auth';

export const LoginForm = ({ afterSubmit }: { afterSubmit: () => void }) => {
  const { signIn, error } = useAuth();
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep(2);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await signIn(email, password);
    afterSubmit();
  };

  const handleMagicLinkClick = async () => {
    try {
      await authService.requestMagicLink({ email });
      enqueueSnackbar('Magic link sent! Check your email to log in.', {
        variant: 'success',
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to send magic link';
      enqueueSnackbar(message, {
        variant: 'error',
      });
    } finally {
      afterSubmit();
    }
  };

  const handleBack = () => {
    setStep(1);
    setPassword('');
  };

  if (step === 1) {
    return (
      <AuthForm
        title="Log In"
        onSubmit={handleEmailSubmit}
        submitLabel="Continue"
        error={error}
      >
        <TextField
          label="Email"
          type="email"
          value={email}
          name="email"
          onChange={(e) => setEmail(e.target.value)}
          required
          fullWidth
          autoComplete="email"
          autoFocus
        />
      </AuthForm>
    );
  }

  return (
    <AuthForm title="Log In" onSubmit={handlePasswordSubmit} error={error}>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {email}
        </Typography>
        <Button
          onClick={handleBack}
          size="small"
          sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
        >
          Change email
        </Button>
      </Box>
      <TextField
        label="Password"
        type="password"
        name="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        fullWidth
        autoComplete="current-password"
        autoFocus
      />
      <Button
        type="submit"
        variant="contained"
        color="secondary"
        size="large"
        fullWidth
        sx={{ mt: 2 }}
      >
        Log In
      </Button>
      <Divider sx={{ my: 1 }}>or</Divider>
      <Typography
        sx={{ textAlign: 'center' }}
        variant="body2"
        color="text.secondary"
        gutterBottom
      >
        Or use a magic link to log in:
      </Typography>
      <Button
        variant="outlined"
        color="secondary"
        fullWidth
        onClick={handleMagicLinkClick}
        type="button"
        startIcon={<AutoFixHighIcon />}
      >
        Email Me a Link
      </Button>
    </AuthForm>
  );
};
